import { initialDataProvider, TUser } from '@microfrontends/app-shell-v2'
import { Button, Col, CompanyCardSquare, CompanyIndustry, ELanguage } from '@microfrontends/react-components'
import { useRef, useState } from 'react'
import { getCompanyList } from '../../services/companyListService'
import { useStore } from '../../store'
import { ACTION_SET_COMPANY_LIST } from '../../store/actions'
import { ContentWrapper } from '../Banner/Banner.css'
import { ICompanyListItem } from '../CompanyListPage/type'
import { ClearKeyword, Keyword, KeywordCount, NoResultWrapper } from '../CompanySearchResult/CompanySearchResult.css'
import { LIMIT_COMPANY_LIST } from '../constants'
import NoResultIcon from '../Icons/NoResultIcon'
import { CompanyFilter, CompanyListSectionWrapper, CompanySlider, LoadMoreAction } from './CompanyListSection.css'
import translations from './translation'

export interface ICompanyListSectionProps {
    title: string
    langCode: ELanguage
}
const CompanyListSection = (props: ICompanyListSectionProps) => {
    const { title, langCode } = props
    const { state, dispatch } = useStore()
    const { isMobile, user, companyList } = state

    // Industries Filter
    const [selectedIndustries, setSelectedIndustries] = useState<number[]>([-1])
    // Pagination, load more button
    const [currentPage, setCurrentPage] = useState<number>(0)
    const [isShowLoadMoreButton, setShowLoadMoreButton] = useState<boolean>(true)
    const currentCompanyCount = useRef<number>(0)
    const t = (text: string): string => {
        if (langCode === ELanguage.EN) {
            return text
        } else {
            return translations[text]
        }
    }

    const handleOnChangeFilterIndustries = (industryId: number[] | number) => {
        setSelectedIndustries(Array.isArray(industryId) ? industryId : [industryId])
        dispatch({
            type: ACTION_SET_COMPANY_LIST,
            payload: []
        })
        getMoreCompanies(0, Array.isArray(industryId) ? industryId : [industryId])
    }

    const getMoreCompanies = async (page: number, industries: number[]) => {
        setShowLoadMoreButton(false)
        const rangesFilter = [
            {
                field: 'companyId',
                operator: '>',
                value: '0'
            }
        ]
        setCurrentPage(page)
        const filters = [
            {
                field: 'profilePublishedSiteMask',
                value: '1,3'
            }
        ]
        if (industries.length > 0 && industries[0] !== -1) {
            filters.push({
                field: 'industriesV3CCP.industryV3Id',
                value: industries.join(',')
            })
        }
        // lastPublishedOn desc
        const featuredCompanyList = await getCompanyList(
            {
                query: '',
                order: [
                    {
                        field: 'lastPostJobOn',
                        value: 'desc'
                    }
                ],
                hitsPerPage: LIMIT_COMPANY_LIST,
                filter: filters,
                page: page,
                ranges: rangesFilter,
                userId: user?.userId,
                regexp: [
                    {
                        field: 'companySlug',
                        value: '.+'
                    }
                ],
                isJobs: true
            },
            langCode,
            user?.accessToken || ''
        )
        // Merge company reponse
        let companyResponseList = [...companyList]
        if (page === 0) {
            companyResponseList = [featuredCompanyList]
        } else {
            companyResponseList.push(featuredCompanyList)
        }

        dispatch({
            type: ACTION_SET_COMPANY_LIST,
            payload: companyResponseList
        })
        // Count total display company
        let totalCompanies = 0
        companyResponseList.map((companyResponse) => {
            totalCompanies += companyResponse.data.length
        })
        currentCompanyCount.current = totalCompanies
        // show load more button if total companies count is smaller than limit
        const latestCompany = companyResponseList[companyResponseList.length - 1]
        if (totalCompanies < (latestCompany?.meta?.nbHits || 0)) {
            setShowLoadMoreButton(true)
        }
    }

    const onClickViewMore = () => {
        const latestCompany = companyList[companyList.length - 1]
        if (currentCompanyCount.current >= (latestCompany?.meta?.nbHits || 0)) {
            return
        }
        // Do get more
        getMoreCompanies(currentPage + 1, selectedIndustries)
    }

    const onClearFilter = () => {
        setSelectedIndustries([-1])
        dispatch({
            type: ACTION_SET_COMPANY_LIST,
            payload: []
        })
        getMoreCompanies(0, [-1])
    }

    const companyListMapped: ICompanyListItem[] = []
    companyList.map((companyResponse) => {
        companyResponse?.data.map((companyInfo) => companyListMapped.push(companyInfo))
    })
    // Get list company name
    let mappedCompaniesName: string[] = []
    if (companyListMapped.length === 0) {
        const industries = initialDataProvider.getIndustries()
        mappedCompaniesName = selectedIndustries.map((industryId) => {
            if (industries.length > 0) {
                return langCode === ELanguage.EN ? industries[0].nameEn : industries[0].name
            }
            return null
        }) as string[]
    }

    return (
        <CompanyListSectionWrapper>
            <ContentWrapper isMarginTop hasBg hasBorder>
                <h2>
                    {title} <span>({(companyList[0] && companyList[0]?.meta?.nbHits) || 0})</span>
                </h2>
                <CompanyFilter>
                    <CompanyIndustry
                        options={state.companyIndustries}
                        value={selectedIndustries ? selectedIndustries[0] : -1}
                        placement='bottom-end'
                        dropdownStyle={{ minWidth: langCode === ELanguage.EN ? 450 : 400 }}
                        langCode={langCode}
                        isMobile={isMobile}
                        onChange={(e) => handleOnChangeFilterIndustries(e)}
                    />
                </CompanyFilter>
                <CompanySlider gutter={16}>
                    {companyListMapped.length === 0 ? (
                        <Col>
                            <NoResultWrapper>
                                <NoResultIcon />
                                <p>
                                    {t('We found')} <KeywordCount>0</KeywordCount> {t('company matching with')}{' '}
                                    {t(mappedCompaniesName.length > 1 ? 'industries' : 'industry')}{' '}
                                    <Keyword>&quot;{mappedCompaniesName.join(', ')}&quot;</Keyword>
                                </p>
                                <ClearKeyword onClick={onClearFilter}>{t('Clear filter')}</ClearKeyword>
                            </NoResultWrapper>
                        </Col>
                    ) : (
                        companyListMapped?.map((companyItem: ICompanyListItem, index: number) => {
                            return (
                                <Col sm={8} key={`company-item-${index}`}>
                                    <CompanyCardSquare
                                        data-id={`index-${index}`}
                                        companyInfo={companyItem}
                                        user={user as TUser}
                                        langCode={langCode}
                                        baseMsUrl={process.env.NEXT_PUBLIC_MS_BASE_PATH}
                                        baseImgUrl={process.env.NEXT_PUBLIC_IMAGE_URL}
                                        displayCompanyEvenHaveNoJob
                                        decryptToken
                                        initData={{
                                            locations: initialDataProvider.getLocations(),
                                            jobFunctions: initialDataProvider.getJobFunction(),
                                            companyIndustries: initialDataProvider.getIndustries(),
                                            jobLevels: initialDataProvider.getLevels(),
                                            educations: initialDataProvider.getHighestDegrees(),
                                            countries: initialDataProvider.getCountries(),
                                            cities: initialDataProvider.getLocations(),
                                            districts: initialDataProvider.getDistricts(),
                                            benefits: initialDataProvider.getBenefits()
                                        }}
                                    />
                                </Col>
                            )
                        })
                    )}
                </CompanySlider>
                <LoadMoreAction
                    style={{
                        display: isShowLoadMoreButton ? 'flex' : 'none'
                    }}
                >
                    <Button onClick={onClickViewMore}>{t('View more')}</Button>
                </LoadMoreAction>
            </ContentWrapper>
        </CompanyListSectionWrapper>
    )
}

export default CompanyListSection
